<script setup lang="ts">
import type { INumberDayItemDataClientDTO } from '~/shared/api/client/number-day/number-day.dto';
import { resizeImageSetLayers } from '~/shared/lib/resizeImage';

const props = defineProps<{ items: INumberDayItemDataClientDTO[] }>();

const itemNews = computed(() => props.items[0]!);
const itemInfo = computed(() => props.items[1]!);

const isRedirect = computed(() =>
  Boolean(
    itemNews.value.news.redirect && itemNews.value.news.redirect.length > 0,
  ),
);

const link = computed(() =>
  isRedirect.value && itemNews.value.news.redirect
    ? itemNews.value.news.redirect
    : itemNews.value
      ? `/${itemNews.value.news.type}/${itemNews.value.news.id}`
      : '/',
);
const categoryLink = `/category/${itemNews.value.news.category?.id}`;

const newsImageUrl = computed(
  () =>
    itemNews.value.image_one?.file_url ??
    itemNews.value.news.image?.photo?.file_url,
);
const newsImageStyle = computed(() =>
  resizeImageSetLayers(newsImageUrl.value, {
    width: 360,
    height: 346,
    mode: 'fill',
  })
    .map((layer) => `background-image: ${layer}`)
    .join('; '),
);
const digitImageStyle = computed(() =>
  resizeImageSetLayers(itemInfo.value.image_one?.file_url, {
    width: 720,
    height: 346,
    mode: 'fill',
  })
    .map((layer) => `background-image: ${layer}`)
    .join('; '),
);
</script>

<template v-if="itemNews && itemInfo">
  <div :class="$style.item">
    <NuxtLink
      :to="link"
      :class="$style.item__link"
      :style="newsImageStyle"
      :target="isRedirect ? '_blank' : undefined"
    >
      <div
        :class="$style.item__category"
        @click.stop="navigateTo(categoryLink)"
      >
        {{ itemNews.news.category?.name }}
      </div>
      <span :class="$style.item__title">
        {{ itemNews.news.title }}
      </span>
    </NuxtLink>

    <div :class="$style.item__digit" :style="digitImageStyle">
      <div :class="$style.item__headerText">Число дня</div>
      <div :class="$style.item__fact">{{ itemInfo.title }}</div>
      <div :class="$style.item__footerText">{{ itemInfo.subject }}</div>
    </div>
  </div>
</template>

<style module lang="scss">
.item {
  display: flex;
  flex-flow: row nowrap;
  height: 346px;

  &__digit {
    padding: var(--space-2-5x);
    background-repeat: no-repeat;
    background-size: cover;
    padding-top: 60px;
    text-align: center;
    flex-grow: 1;
  }

  &__link {
    display: flex;
    flex-flow: column nowrap;
    width: 360px;
    height: 100%;
    background-repeat: no-repeat;
    background-position: center;
    background-size: cover;
    text-decoration: none;
    text-align: center;
    justify-content: space-between;
    cursor: pointer;

    padding: var(--space-2-5x) var(--space-2x);
    position: relative;

    &::before {
      position: absolute;
      content: '';
      left: 0px;
      right: 0px;
      top: 0px;
      bottom: 0px;

      background: #111111;

      opacity: 0.4;
    }
  }

  &__category {
    @include text_caption2_caps;
    color: var(--color-white);
    z-index: 1;
  }

  &__title {
    @include text_h5;
    color: var(--color-white);
    z-index: 1;
  }

  &__headerText {
    @include text_caption2_caps;
    color: var(--color-white);
  }

  &__fact {
    @include text_golos;
    color: var(--color-white);
    margin-top: 18px;
    margin-bottom: var(--space-1-5x);
  }

  &__footerText {
    @include text_body2;
    color: var(--color-white);
  }
}

@media (hover: hover) {
  .item__link {
    transition: all 0.2s;

    &:hover {
      opacity: 0.85;
    }
  }
}

@media (min-width: 768px) and (max-width: 1379px) {
  .item {
    &__link {
      width: 308px;
    }
  }
}

@media (max-width: 767px) {
  .item {
    flex-direction: column;
    height: inherit;

    &__link {
      width: 100%;
    }

    &__digit {
      height: 346px;
    }
  }
}

@media (min-width: 480px) and (max-width: 767px) {
  .item {
    &__link {
      height: 346px;
    }

    &__digit {
      height: 346px;
    }
  }
}

@media (max-width: 479px) {
  .item {
    &__link {
      height: 260px;
    }

    &__digit {
      padding-left: var(--space-2x);
      padding-right: var(--space-2x);
      padding-top: 40px;
      height: 260px;
    }

    &__fact {
      font-size: 100px;
    }

    &__title {
      @include text_body1_large_medium;
    }
  }
}
</style>
